import { observer } from 'mobx-react'
import { useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router'

// evertel
import { validateEmail } from '@evertel/utils'
import { Card, CardBody, Col, Button, Icon, Row, useUI, IconLink, FormInput, Logo } from '@evertel/web/ui'
import {
    EmailVerificationController,
    BC_CHANNEL_NAME,
    BC_MESSAGES,
    BCMessageType
} from '@evertel/shared/feature-authentication-auth0'
import { useService } from '@evertel/di'
import { Analytics } from '@evertel/analytics'
import { RegistrationData } from '../RegistrationStepperUnauthorized'

interface EmailVerificationStepProps {
    registrationData: RegistrationData
    onEmailUpdate?: (newEmail: string) => void
    onStepComplete: () => void
    isCrisis?: boolean
}

const Step2EmailVerification: React.FC<EmailVerificationStepProps> = observer(({
    registrationData,
    onEmailUpdate,
    onStepComplete
}) => {
    const analytics = useService(Analytics)
    const emailVerificationController = useService(EmailVerificationController, [])

    const { addToast } = useUI()
    const navigate = useNavigate()

    const bc = useRef<BroadcastChannel | null>(null)

    const [isResending, setIsResending] = useState(false)
    const [verificationComplete, setVerificationComplete] = useState(false)
    const [changeEmailView, setChangeEmailView] = useState(false)
    const [newEmail, setNewEmail] = useState('')
    const [isSavingEmail, setIsSavingEmail] = useState(false)

    const { email, password } = registrationData

    useEffect(() => {
        // Store email in localStorage for resolve page fallback
        if (email && !localStorage.getItem('email')) {
            localStorage.setItem('email', email)
        }

        bc.current = new BroadcastChannel(BC_CHANNEL_NAME)

        // WAITING PAGE: Listen for pings and respond
        bc.current.onmessage = (event) => {
            const message = event.data as BCMessageType

            if (message === BC_MESSAGES.PING) {
                // Respond to identify ourselves as the registration waiting page
                bc.current?.postMessage(BC_MESSAGES.PONG_REGISTRATION)
            }
        }

        return () => {
            bc.current?.close()
        }
    }, [email])

    useEffect(() => {
        // initialize controller
        if (email) emailVerificationController.init(email)

        return () => {
            emailVerificationController.stopGetIsVerifiedPoll()
        }

    }, [email, emailVerificationController])

    useEffect(() => {
        if (emailVerificationController.isVerified) {
            setVerificationComplete(true)

            // remove email from localStorage
            if (localStorage.getItem('email')) {
                localStorage.removeItem('email')
            }
        }

    }, [emailVerificationController.isVerified])

    const resendVerificationEmail = async () => {
        setIsResending(true)

        try {
            await emailVerificationController.resendVerificationEmail()
            addToast({
                color: 'success',
                message: 'A new verification email has been sent'
            })
        } catch (error) {
            addToast({
                title: 'Error',
                color: 'danger',
                message: error.message || String(error)
            })
        } finally {
            setIsResending(false)
        }
    }

    const handleSaveAndResendEmail = async () => {
        const newEmailTrim = newEmail?.trim()
        const registrationEmailTrim = registrationData.email.trim()

        if (!newEmailTrim || newEmailTrim === registrationEmailTrim) {
            addToast({
                color: 'warning',
                message: 'No changes made to email'
            })
            setChangeEmailView(false)
            return
        }

        setIsSavingEmail(true)

        try {
            await onEmailUpdate?.(newEmailTrim)
            setChangeEmailView(false)
        } catch (error: any) {
            addToast({
                title: 'Error',
                color: 'danger',
                message: error.message || error
            })
        } finally {
            setIsSavingEmail(false)
        }
    }

    const goToLogin = () => {
        navigate('/login')
    }

    const handleEmailChange = (email: string) => {
        setNewEmail(email)
    }

    const renderContent = () => {
        if (changeEmailView) {
            return (
                <ChangeEmailView
                    email={newEmail}
                    onEmailChange={handleEmailChange}
                    onSaveAndResend={handleSaveAndResendEmail}
                    isLoading={isSavingEmail}
                    onGoBack={() => setChangeEmailView(false)}
                />
            )
        }

        if (verificationComplete) {
            return (
                <VerificationCompleteView
                    onStepComplete={onStepComplete}
                />
            )
        }

        return (
            <VerifyEmailView
                email={email}
                onResendEmail={resendVerificationEmail}
                onChangeEmailView={() => {
                    setChangeEmailView(true)
                    setNewEmail(registrationData.email)
                }}
                onGoToLogin={goToLogin}
                isResending={isResending}
            />
        )
    }

    return (
        <Row className="fadeIn animated">
            <Card className="mx-4">
                <CardBody className="p-4">
                    {renderContent()}
                </CardBody>
            </Card>
        </Row>
    )
})

// ============================================================================
// CHANGE EMAIL VIEW
// ============================================================================

interface ChangeEmailViewProps {
    email: string
    onEmailChange: (email: string) => void
    onSaveAndResend: () => void
    isLoading?: boolean
    onGoBack: () => void
}

const ChangeEmailView: React.FC<ChangeEmailViewProps> = ({
    email,
    onEmailChange,
    onSaveAndResend,
    isLoading = false,
    onGoBack
}) => {
    const [emailError, setEmailError] = useState('')
    const [isEmailValid, setIsEmailValid] = useState(true)
    const [localEmail, setLocalEmail] = useState(email)

    const validateEmailField = () => {
        if (!isEmailValid && localEmail) {
            setEmailError('Email seems to be invalid')
        } else {
            setEmailError('')
        }
    }

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setLocalEmail(value)
        onEmailChange(value)
        setIsEmailValid(validateEmail(value))
    }

    const handleSaveAndResend = () => {
        if (isEmailValid) {
            onSaveAndResend()
        }
    }

    return (
        <div className="pb-3">
            <h1 className="mb-4 text-center">Please verify your email address</h1>
            <p className="h6">Change your email and get a new verification link</p>

            <FormInput
                name="email"
                value={localEmail}
                label="Email"
                placeholder="Enter your new Email"
                className="mt-4"
                onChange={handleEmailChange}
                onBlur={validateEmailField}
                error={emailError}
            />

            <div className="d-flex flex-column justify-content-center">
                <Button
                    className="w-100 mt-4"
                    color="primary"
                    loading={isLoading}
                    disabled={isLoading || !isEmailValid || !localEmail}
                    onClick={handleSaveAndResend}
                >
                    Save and Resend Verification Email
                </Button>
                <Button
                    color="link"
                    block
                    className="mt-2"
                    onClick={onGoBack}
                >
                    Cancel
                </Button>
            </div>
        </div>
    )
}

// ============================================================================
// VERIFY EMAIL VIEW (waiting state)
// ============================================================================

interface VerifyEmailViewProps {
    email: string
    onResendEmail: () => void
    onChangeEmailView: () => void
    onGoToLogin: () => void
    isResending: boolean
}

const VerifyEmailView: React.FC<VerifyEmailViewProps> = ({
    email,
    onResendEmail,
    onChangeEmailView,
    onGoToLogin,
    isResending
}) => {
    return (
        <div className="pb-3">
            <div className="text-center mb-4">
                <Logo />
            </div>
            <h1 className="mb-4 text-center">Please verify your email address</h1>
            <p className="h6">Check your email and click on the verification link to continue.</p>

            <div className="d-flex justify-content-center flex-column">
                <div className="email-box ps-2 pe-2 mt-2 mb-2">
                    <div className="h6 text-center mt-4 mb-3">
                        <p className="mb-2">We sent a verification email to:</p>
                        <span className="fw-bold">
                            {email}
                        </span>
                        <IconLink
                            icon={{
                                name: 'edit',
                                size: 16,
                                color: 'primary'
                            }}
                            onClick={onChangeEmailView}
                        />
                    </div>
                </div>

                <Button
                    color="primary"
                    type="submit"
                    outline
                    loading={isResending}
                    disabled={isResending}
                    className="mt-2 text-center"
                    onClick={onResendEmail}
                >
                    Resend Verification Email
                </Button>
                <Button
                    color="link"
                    block
                    className="mt-2"
                    onClick={onGoToLogin}
                >
                    Logout
                </Button>
            </div>

            <div className="mt-4">
                <small>
                    <strong>Didn't receive an email?</strong>
                    {' make sure your email is correct above (no typos).'}
                    <br/>
                    If it is, check your SPAM folder or try adding no-reply@getevertel.com to your contacts or Safe Senders list.
                </small>
            </div>
        </div>
    )
}

// ============================================================================
// VERIFICATION COMPLETE VIEW
// ============================================================================

interface VerificationCompleteViewProps {
    onStepComplete: () => void
}

const VerificationCompleteView: React.FC<VerificationCompleteViewProps> = ({
    onStepComplete
}) => {
    const [isContinuing, setIsContinuing] = useState<boolean>(false)

    const handleContinue = () => {
        setIsContinuing(true)
        onStepComplete()
    }

    return (
        <Row className="mb-3 fadeIn animated">
            <Col className="text-center">
                <Icon
                    name="check-circle"
                    color="success"
                    style={{ fontSize: 60, marginBottom: 25 }}
                />
                <h1 className="mb-4">Your email has been verified!</h1>
                <p className="h3">Please continue to complete your account setup</p>
                <Button
                    color="primary"
                    type="submit"
                    loading={isContinuing}
                    disabled={isContinuing}
                    onClick={handleContinue}
                    className="mt-5"
                >
                    Continue
                </Button>
            </Col>
        </Row>
    )
}

export { Step2EmailVerification }
